import i18n, {
  type BackendModule,
  type ReadCallback,
  type Resource,
  type ResourceKey,
} from 'i18next';
import { initReactI18next } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/te';
import 'dayjs/locale/hi';
import LanguageDetector from 'i18next-browser-languagedetector';
import {
  DEFAULT_LANGUAGE,
  DEFAULT_NAMESPACE,
  LANGUAGE_STORAGE_KEY,
  STARTUP_NAMESPACES,
  SUPPORTED_LANGUAGE_CODES,
  getLanguage,
} from './config';

/**
 * Namespaces that render before any route resolves (app shell, nav, login, error toasts)
 * are bundled into the initial chunk. Lazy-loading them would flash raw keys on first paint.
 */
const eagerModules = import.meta.glob('./locales/*/{common,nav,auth,errors}.json', {
  eager: true,
  import: 'default',
}) as Record<string, ResourceKey>;

/**
 * Everything else loads on demand. Vite splits each JSON into its own hashed chunk, so a
 * receptionist who never opens a lab screen never downloads the `lab` bundle — which matters
 * because the full translated string set is far too large to ship in one payload.
 */
const lazyModules = import.meta.glob('./locales/*/*.json', {
  import: 'default',
}) as Record<string, () => Promise<ResourceKey>>;

/**
 * Under test, every catalogue is loaded eagerly and the on-demand backend is not installed.
 *
 * Namespace availability is otherwise asynchronous, so a component rendered before its namespace
 * resolves shows raw key paths — a test would assert on "Group" and receive "group". That is a
 * harness artefact rather than a product defect, but it is nondeterministic, which is worse: it
 * makes the suite intermittently blind to genuine translation regressions. Tests are the one
 * place where loading a megabyte of JSON costs nothing.
 */
const IS_TEST = import.meta.env.MODE === 'test';

const allModules = import.meta.glob('./locales/*/*.json', {
  eager: true,
  import: 'default',
}) as Record<string, ResourceKey>;

function buildResources(modules: Record<string, ResourceKey>): Resource {
  const resources: Resource = {};
  for (const [path, mod] of Object.entries(modules)) {
    const match = path.match(/\.\/locales\/([^/]+)\/([^/]+)\.json$/);
    if (!match) continue;
    const [, lng, ns] = match;
    (resources[lng] ??= {})[ns] = mod;
  }
  return resources;
}

/** Every namespace present on disk — used to declare `ns` up front in the test environment. */
function allNamespaces(): string[] {
  const set = new Set<string>();
  for (const path of Object.keys(allModules)) {
    const match = path.match(/\.\/locales\/[^/]+\/([^/]+)\.json$/);
    if (match) set.add(match[1]);
  }
  return [...set];
}

const lazyBackend: BackendModule = {
  type: 'backend',
  init: () => {},
  read(language: string, namespace: string, callback: ReadCallback) {
    const loader = lazyModules[`./locales/${language}/${namespace}.json`];
    if (!loader) {
      // Not an error: a namespace may legitimately exist in `en` only while its Telugu
      // translation is still in clinical review. i18next falls back to `fallbackLng`.
      callback(null, {});
      return;
    }
    loader()
      .then((resource) => callback(null, resource))
      .catch((err) => callback(err as Error, null));
  },
};

/**
 * Applied to <html> so that (a) screen readers announce the right language and
 * (b) the theme can select a font stack that actually contains the script's glyphs.
 */
export function applyDocumentLanguage(code: string): void {
  const lang = getLanguage(code);
  document.documentElement.lang = lang.code;
  document.documentElement.setAttribute('data-script', lang.script);
  // Dates rendered with dayjs (booking times, "Mon, 3 Feb") follow the UI language too.
  // Every supported code has a matching dayjs locale imported above.
  dayjs.locale(lang.code === 'te' || lang.code === 'hi' ? lang.code : 'en');
}

const instance = IS_TEST ? i18n : i18n.use(lazyBackend);

void instance
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: buildResources(IS_TEST ? allModules : eagerModules),
    partialBundledLanguages: true,
    supportedLngs: SUPPORTED_LANGUAGE_CODES,
    fallbackLng: DEFAULT_LANGUAGE,
    defaultNS: DEFAULT_NAMESPACE,
    ns: IS_TEST ? allNamespaces() : STARTUP_NAMESPACES,
    load: 'languageOnly',
    detection: {
      order: ['localStorage', 'navigator'],
      lookupLocalStorage: LANGUAGE_STORAGE_KEY,
      // The detector READS the stored preference but does not write it back: its own cache
      // write is unguarded, so on a browser with storage blocked (private mode, or site data
      // disabled) simply clicking the language button threw out of changeLanguage and the
      // language never changed. `setLanguage` in useAppLanguage.ts owns the write instead,
      // inside a try/catch, so a blocked store degrades to "changes for this session only".
      caches: [],
    },
    interpolation: {
      // React escapes for us; double-escaping mangles names with apostrophes and & in them.
      escapeValue: false,
    },
    // A missing key must render its English fallback, never an empty string — a blank
    // button or a blank dose label is worse than an untranslated one.
    returnEmptyString: false,
    saveMissing: false,
    react: {
      useSuspense: false,
    },
  });

i18n.on('languageChanged', applyDocumentLanguage);
applyDocumentLanguage(i18n.language || DEFAULT_LANGUAGE);

export default i18n;
